<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';

import { searchContratos } from 'dashboard/api/supra';

const router = useRouter();
const route = useRoute();

const { t } = useI18n();

// State
const searchQuery = ref('');
const isLoading = ref(false);
const contracts = ref([]);
const error = ref(null);

const parseSupraContrato = data => {
  if (!data?.contrato) return null;
  return {
    numeroContrato: data.contrato,
    explotacion: data.explotacion,
    titular: data.nombreTitular,
    uso: data.tipoUso || data.tarifa,
    estado: data.estado === 'activo' ? 'Activo' : 'Inactivo',
    numeroContador: data.numeroMedidor,
    direccion: data.calle
      ? `${data.calle} ${data.numero || ''}, ${data.municipio || ''}`.trim()
      : data.direccion || '',
    fechaAlta: data.fechaAlta,
  };
};

// Methods
const searchContracts = async () => {
  if (!searchQuery.value.trim()) {
    useAlert(t('CONTRACTS.SEARCH_EMPTY'));
    return;
  }

  isLoading.value = true;
  error.value = null;
  contracts.value = [];

  const trimmedQuery = searchQuery.value.trim();

  try {
    const res = await searchContratos(trimmedQuery);
    const rows = res.data?.data;
    const parsed = Array.isArray(rows)
      ? rows.map(parseSupraContrato).filter(Boolean)
      : [];

    if (parsed.length) {
      contracts.value = parsed;
    } else {
      useAlert(t('CONTRACTS.NO_RESULTS'));
    }
  } catch (err) {
    if (err.response?.status === 404) {
      useAlert(t('CONTRACTS.NO_RESULTS'));
    } else {
      error.value = err.message || t('CONTRACTS.SEARCH_ERROR');
      useAlert(error.value);
    }
  } finally {
    isLoading.value = false;
  }
};

const navigateToDetail = contract => {
  if (!contract || !contract.numeroContrato) return;

  // Guardar explotación en localStorage para uso posterior
  if (contract.explotacion) {
    localStorage.setItem(
      `contrato_${contract.numeroContrato}_explotacion`,
      contract.explotacion
    );
  }

  const accountId = route.params.accountId;
  const contratoId = contract.numeroContrato;

  router
    .push({
      name: 'contract_detail',
      params: { accountId, contratoId },
    })
    .catch(() => {
      // Fallback to full path
      router.push(`/app/accounts/${accountId}/contracts/${contratoId}`);
    });
};

</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-background">
    <!-- Header -->
    <header
      class="flex items-center justify-between p-4 border-b border-n-weak"
    >
      <div class="flex items-center gap-3">
        <h1
          class="text-xl font-semibold text-n-slate-12 underline decoration-n-brand"
        >
          {{ $t('CONTRACTS.HEADER') }}
        </h1>
      </div>
    </header>

    <!-- Search Section -->
    <div class="p-4 border-b border-n-weak bg-n-solid-3">
      <div class="flex items-center gap-2">
        <div
          class="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg bg-n-background border border-n-weak"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <span class="text-n-slate-11 font-mono">#</span>
          <Input
            :model-value="searchQuery"
            type="text"
            :placeholder="$t('CONTRACTS.SEARCH_PLACEHOLDER')"
            custom-input-class="flex-1 border-none bg-transparent outline-none"
            class="flex-1"
            @update:model-value="searchQuery = $event"
            @enter="searchContracts"
          />
        </div>
        <Button
          color="blue"
          :disabled="isLoading || !searchQuery.trim()"
          @click="searchContracts"
        >
          {{ $t('CONTRACTS.SEARCH_BUTTON') }}
        </Button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-auto p-4 bg-n-bg">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-12">
        <Spinner :size="32" />
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center py-12 gap-4"
      >
        <span class="i-lucide-alert-circle text-6xl text-n-ruby-11" />
        <p class="text-n-slate-11">{{ error }}</p>
        <Button color="blue" @click="searchContracts">
          {{ $t('CONTRACTS.RETRY') }}
        </Button>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!contracts.length && !isLoading"
        class="flex flex-col items-center justify-center py-12 gap-4"
      >
        <span class="i-lucide-file-text text-6xl text-n-slate-10" />
        <p class="text-n-slate-11">{{ $t('CONTRACTS.EMPTY_STATE') }}</p>
      </div>

      <!-- Contracts List -->
      <div v-else-if="contracts.length > 0" class="space-y-4">
        <div
          v-for="(contract, index) in contracts"
          :key="index"
          class="p-4 rounded-lg border border-n-weak bg-n-background hover:bg-n-alpha-1 cursor-pointer transition-colors"
          @click.stop.prevent="navigateToDetail(contract)"
        >
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-n-slate-12">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                #{{ contract.numeroContrato }}
              </h3>
              <span
                class="px-2 py-1 text-xs rounded"
                :class="
                  contract.estado === 'Activo'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                "
              >
                {{ contract.estado }}
              </span>
            </div>
            <div
              v-if="contract.titular"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ contract.titular }}
            </div>
            <div v-if="contract.direccion" class="text-sm text-n-slate-11">
              <span class="i-lucide-map-pin inline-block mr-1" />{{
                contract.direccion
              }}
            </div>
            <div class="flex flex-wrap gap-3 text-xs text-n-slate-11">
              <span v-if="contract.uso">
                <span class="font-medium">{{
                  $t('CONTRACTS.LIST.USE_LABEL')
                }}</span>
                {{ contract.uso }}
              </span>
              <span v-if="contract.numeroContador">
                <span class="font-medium">{{
                  $t('CONTRACTS.LIST.METER_LABEL')
                }}</span>
                {{ contract.numeroContador }}
              </span>
              <span v-if="contract.explotacion">
                <span class="font-medium">{{
                  $t('CONTRACTS.LIST.EXPLOITATION_LABEL')
                }}</span>
                {{ contract.explotacion }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
